import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  DatePicker,
  TimePicker,
  Space,
  message,
  Row,
  Col,
  Card,
  Typography
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import {
  createSchedule,
  updateSchedule,
  saveManualScheduleEdit,
  normalizeDate,
  JOB_TYPES,
  JOB_TYPE_LABELS,
  SCHEDULE_STATUSES,
  SCHEDULE_STATUS_LABELS
} from '../../firebase/scheduleService';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const JobScheduleForm = ({ 
  schedule = null, 
  onSave = () => {},
  onCancel = () => {}
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Initialize form with existing schedule data
  useEffect(() => {
    if (schedule) {
      const formValues = {
        // Use scheduledDate/scheduledTime for editing, fallback to legacy jobDate/jobTime
        scheduledDate: schedule.scheduledDate ? dayjs(schedule.scheduledDate) : 
                     (schedule.jobDate ? dayjs(schedule.jobDate) : null),
        scheduledTime: schedule.scheduledTime ? dayjs(schedule.scheduledTime, 'HH:mm') : 
                      (schedule.jobTime ? dayjs(schedule.jobTime, 'HH:mm') : null),
        jobType: schedule.jobType,
        customerName: schedule.customerName,
        companyName: schedule.companyName,
        contactPerson: schedule.contactPerson,
        phone: schedule.phone,
        siteAddress: schedule.siteAddress,
        configuration: schedule.configuration,
        status: schedule.status,
        notes: schedule.notes
      };
      form.setFieldsValue(formValues);
    } else {
      // Set defaults for new schedule
      form.setFieldsValue({
        jobType: JOB_TYPES.DELIVER_ONLY,
        status: SCHEDULE_STATUSES.CONFIRMED
      });
    }
  }, [schedule, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const scheduleData = {
        // Dual date system: use scheduledDate/scheduledTime for actual scheduling
        scheduledDate: values.scheduledDate ? values.scheduledDate.format('YYYY-MM-DD') : '',
        scheduledTime: values.scheduledTime ? values.scheduledTime.format('HH:mm') : '',
        // Legacy fields for backward compatibility
        jobDate: values.scheduledDate ? values.scheduledDate.format('YYYY-MM-DD') : '',
        jobTime: values.scheduledTime ? values.scheduledTime.format('HH:mm') : '',
        jobType: values.jobType,
        customerName: values.customerName || '',
        companyName: values.companyName || '',
        contactPerson: values.contactPerson || '',
        phone: values.phone || '',
        siteAddress: values.siteAddress || '',
        configuration: values.configuration || '',
        status: values.status,
        notes: values.notes || '',
        isManualEntry: true, // Mark as manual entry
        invoiceId: schedule?.invoiceId || '', // Keep existing invoice link if editing
        invoiceNumber: schedule?.invoiceNumber || '',
        // Keep invoice date unchanged (read-only)
        invoiceDate: schedule?.invoiceDate || ''
      };

      if (schedule) {
        // For updates, ALWAYS use saveManualScheduleEdit for date/time/notes to ensure protection flags are set
        console.log('✏️ Updating existing schedule - ensuring manual edit protection');
        
        // CRITICAL FIX: Always save date/time fields as manual edits to set protection flags
        // This ensures that sync operations will never overwrite these fields
        const manualEdits = {
          scheduledDate: scheduleData.scheduledDate, // Always save as manual edit
          scheduledTime: scheduleData.scheduledTime, // Always save as manual edit  
          notes: scheduleData.notes // Always save as manual edit
        };
        
        console.log('🔒 Setting manual edit protection for:', {
          scheduledDate: manualEdits.scheduledDate || 'empty',
          scheduledTime: manualEdits.scheduledTime || 'empty',
          notes: manualEdits.notes ? 'updated' : 'empty'
        });
        
        // Use saveManualScheduleEdit to set protection flags
        await saveManualScheduleEdit(schedule.id, manualEdits);
        console.log('✅ Manual edit protection flags set successfully');
        
        // For other fields, use the traditional update method
        const otherUpdates = {};
        const fieldsToCheck = ['jobType', 'customerName', 'companyName', 'contactPerson', 'phone', 'siteAddress', 'configuration', 'status'];
        
        fieldsToCheck.forEach(field => {
          const originalValue = schedule[field] || '';
          const newValue = scheduleData[field] || '';
          if (originalValue !== newValue) {
            otherUpdates[field] = newValue;
            console.log(`🔄 Other field changed: ${field}`);
          }
        });
        
        if (Object.keys(otherUpdates).length > 0) {
          // Track these as edited fields for backward compatibility
          const existingEditedFields = schedule.editedFields || [];
          const newEditedFields = [...new Set([...existingEditedFields, ...Object.keys(otherUpdates)])];
          
          await updateSchedule(schedule.id, {
            ...otherUpdates,
            editedFields: newEditedFields,
            hasManualEdits: true
          }, {
            isManualEdit: true,
            editedFields: newEditedFields
          });
          console.log('✅ Other fields updated using traditional API');
        }
        
        message.success('Schedule updated successfully');
        onSave();
      } else {
        // Create new schedule with proper manualEdits structure
        console.log('✨ Creating new manual schedule');
        
        const newScheduleData = {
          ...scheduleData,
          // Set manualEdits structure for new schedules
          manualEdits: {
            scheduledDate: true, // Always true for manual schedules
            scheduledTime: !!scheduleData.scheduledTime, // True if time was provided
            notes: !!scheduleData.notes // True if notes were provided
          },
          // Maintain backward compatibility
          editedFields: [
            'scheduledDate', 
            'scheduledTime', 
            'jobType', 
            'customerName', 
            'companyName',
            'contactPerson', 
            'phone', 
            'siteAddress', 
            'configuration', 
            'status',
            ...(scheduleData.notes ? ['notes'] : [])
          ],
          hasManualEdits: true,
          lastEditedAt: new Date(),
          lastManualEditAt: new Date()
        };
        
        await createSchedule(newScheduleData);
        message.success('Schedule created successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      message.error('Failed to save schedule. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>
            {schedule ? 'Edit Schedule' : 'Add New Manual Schedule'}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            jobType: JOB_TYPES.DELIVER_ONLY,
            status: SCHEDULE_STATUSES.CONFIRMED
          }}
        >
          {/* Job Details Section */}
          <Card title="Job Details" style={{ marginBottom: 16 }} size="small">
            {/* Show invoice date if editing an invoice-linked schedule */}
            {schedule && schedule.invoiceDate && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f0f2f5', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    <strong>Invoice Date:</strong> {schedule.invoiceDate} 
                    {schedule.invoiceNumber && ` (${schedule.invoiceNumber})`}
                    <br />
                    <em>This is the original date from the invoice and cannot be changed. Use the fields below to set your actual scheduled date and time.</em>
                  </div>
                </Col>
              </Row>
            )}
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Scheduled Date"
                  name="scheduledDate"
                  rules={[{ required: true, message: 'Please select scheduled date' }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    placeholder="Select scheduled date"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Scheduled Time (Optional)"
                  name="scheduledTime"
                >
                  <TimePicker 
                    style={{ width: '100%' }}
                    placeholder="Select scheduled time"
                    format="HH:mm"
                    use12Hours={true}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Job Type"
                  name="jobType"
                  rules={[{ required: true, message: 'Please select job type' }]}
                >
                  <Select placeholder="Select job type">
                    {Object.entries(JOB_TYPE_LABELS).map(([key, label]) => (
                      <Option key={key} value={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Schedule Status"
                  name="status"
                  rules={[{ required: true, message: 'Please select schedule status' }]}
                >
                  <Select>
                    {Object.entries(SCHEDULE_STATUS_LABELS).map(([key, label]) => (
                      <Option key={key} value={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Customer Information Section */}
          <Card title="Customer Information" style={{ marginBottom: 16 }} size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Customer Name"
                  name="customerName"
                  rules={[{ required: true, message: 'Please enter customer name' }]}
                >
                  <Input placeholder="Enter customer name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Company Name"
                  name="companyName"
                  rules={[{ required: true, message: 'Please enter company name' }]}
                >
                  <Input placeholder="Enter company name" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Contact Person"
                  name="contactPerson"
                  rules={[{ required: true, message: 'Please enter contact person' }]}
                >
                  <Input placeholder="Enter contact person name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Phone Number"
                  name="phone"
                  rules={[{ required: true, message: 'Please enter phone number' }]}
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Site and Equipment Information */}
          <Card title="Site & Equipment Details" style={{ marginBottom: 16 }} size="small">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Site Address"
                  name="siteAddress"
                  rules={[{ required: true, message: 'Please enter site address' }]}
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Enter the site address where job will be performed" 
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Configuration"
                  name="configuration"
                  rules={[{ required: true, message: 'Please enter equipment configuration' }]}
                >
                  <Input 
                    placeholder="e.g., 3m Platform Single, 6m Platform Double" 
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Additional Notes */}
          <Card title="Additional Information" style={{ marginBottom: 24 }} size="small">
            <Form.Item
              label="Notes"
              name="notes"
            >
              <TextArea
                rows={3}
                placeholder="Enter any additional notes or special instructions..."
              />
            </Form.Item>
          </Card>

          {/* Action Buttons */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Space size="middle">
              <Button onClick={onCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                htmlType="submit" 
                loading={loading}
              >
                {schedule ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default JobScheduleForm;
